import React, { useState, useEffect } from 'react';
import { Alert, Button, Flex, Typography } from 'antd';
import { Info, X, Zap, MonitorUp } from 'lucide-react';
import { DeviceCapabilityInfo } from '../../utils/deviceCapability';
import './DeviceCapabilityHint.scss';

interface DeviceCapabilityHintProps {
  deviceTier: DeviceCapabilityInfo['tier'];
  visible?: boolean;
  onDismiss?: () => void;
}

interface HintConfig {
  icon: React.ReactNode;
  type: 'info' | 'warning' | 'success';
  message: string;
  tip: string;
  showBadge: boolean;
}

const HINT_CONFIGS: Record<DeviceCapabilityInfo['tier'], HintConfig> = {
  low: {
    icon: <MonitorUp size={16} />,
    type: 'warning',
    message: 'Your device is best suited for 720p recording',
    tip: 'Higher resolutions may cause lag or dropped frames. For best results, stick with 720p (HD) quality.',
    showBadge: true,
  },
  medium: {
    icon: <Zap size={16} />,
    type: 'info',
    message: 'Your device can handle up to 1080p recording',
    tip: 'For smooth performance, we recommend starting with 720p. You can upgrade to 1080p if needed.',
    showBadge: false,
  },
  high: {
    icon: <Zap size={16} />,
    type: 'success',
    message: 'Your device supports all quality settings',
    tip: 'Feel free to use any resolution including 2k (Quad HD) for maximum detail.',
    showBadge: false,
  },
};

const STORAGE_KEY = 'device-capability-hint-dismissed';
const STORAGE_EXPIRY_DAYS = 30;

export const DeviceCapabilityHint: React.FC<DeviceCapabilityHintProps> = ({
  deviceTier,
  visible = true,
  onDismiss,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const dismissedData = localStorage.getItem(STORAGE_KEY);
    if (dismissedData) {
      try {
        const { timestamp, tier } = JSON.parse(dismissedData);
        const daysSinceDismiss = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
        
        if (daysSinceDismiss < STORAGE_EXPIRY_DAYS && tier === deviceTier) {
          setIsDismissed(true);
        }
      } catch (error) {
        console.warn('[DeviceCapabilityHint] Failed to parse dismissed data:', error);
      }
    }
  }, [deviceTier]);

  const handleDismiss = () => {
    const dismissData = {
      timestamp: Date.now(),
      tier: deviceTier,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissData));
    setIsDismissed(true);
    onDismiss?.();
    console.log('[DeviceCapabilityHint] Dismissed for 30 days, tier:', deviceTier);
  };

  if (!visible || isDismissed) return null;

  const config = HINT_CONFIGS[deviceTier];

  return (
    <div className="device-capability-hint">
      <Alert
        type={config.type}
        showIcon
        icon={config.icon}
        message={
          <Flex align="center" gap={8}>
            <Typography.Text strong style={{ margin: 0 }}>
              {config.message}
            </Typography.Text>
            {config.showBadge && (
              <span className="device-tier-badge">{deviceTier.toUpperCase()}</span>
            )}
          </Flex>
        }
        description={config.tip}
        closable
        closeIcon={<X size={14} />}
        onClose={handleDismiss}
        className="device-hint-alert"
      />
    </div>
  );
};
